import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FaBus, FaMapMarkerAlt, FaRoute, FaSpinner, FaSearch, FaExclamationTriangle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const RouteDisplayPage = () => {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routePoints, setRoutePoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [geocodingErrors, setGeocodingErrors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const markers = useRef([]);
  const routeLayer = useRef(null);

  // Create custom marker elements
  const createMarkerElement = useCallback((type) => {
    const el = document.createElement('div');
    el.className = 'custom-marker';
    
    if (type === 'start') {
      el.innerHTML = `
        <div class="marker-container start">
          <img src="https://img.icons8.com/ios-filled/50/4CAF50/marker.png" 
               alt="Start" 
               style="width: 32px; height: 32px;"/>
          <div class="pulse-effect"></div>
        </div>
      `;
    } else if (type === 'end') {
      el.innerHTML = `
        <div class="marker-container end">
          <img src="https://img.icons8.com/ios-filled/50/FF5252/marker.png" 
               alt="End" 
               style="width: 32px; height: 32px;"/>
        </div>
      `;
    } else {
      el.innerHTML = `
        <div class="marker-container stop">
          <img src="https://img.icons8.com/ios/50/bus-stop.png" 
               alt="Bus Stop" 
               style="width: 32px; height: 32px;"/>
        </div>
      `;
    }
    
    return el;
  }, []);

  // Fetch all routes from backend
  const fetchRoutes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/routes`);
      setRoutes(res.data);
      if (res.data.length > 0) {
        setSelectedRoute(res.data[0]);
      }
    } catch (err) {
      console.error('Error fetching routes:', err);
      setError('Failed to load routes');
    }
  };

  // Enhanced geocoding with better error handling
  const geocodeLocation = useCallback(async (location, type) => {
    try {
      // Add Lahore, Pakistan for better accuracy
      const query = `${location}, Lahore, Pakistan`;
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
        `access_token=${mapboxgl.accessToken}&limit=1`
      );
      
      if (!response.ok) throw new Error('Geocoding API error');
      
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        return {
          coordinates: data.features[0].center,
          placeName: data.features[0].place_name,
          originalName: location,
          type
        };
      }
      throw new Error('No results found');
    } catch (err) {
      console.error(`Error geocoding ${type} "${location}":`, err);
      setGeocodingErrors(prev => [...prev, { location, type, error: err.message }]);
      return null;
    }
  }, []);

  // Process route with better error handling
  const processRoute = useCallback(async (route) => {
    if (!route) return;

    setLoading(true);
    setError(null);
    setGeocodingErrors([]);

    try {
      // Process all points in parallel
      const points = await Promise.all([
        geocodeLocation(route.startPoint, 'start'),
        ...route.stops.map(stop => geocodeLocation(stop, 'stop')),
        geocodeLocation(route.endPoint, 'end')
      ]);

      // Filter out null results and validate we have at least start and end
      const validPoints = points.filter(point => point !== null);
      
      if (validPoints.length < 2) {
        throw new Error('Could not geocode enough points to display route');
      }

      setRoutePoints(validPoints);
    } catch (err) {
      console.error('Error processing route:', err);
      setError(`Error displaying route: ${err.message}`);
      setRoutePoints([]);
    } finally {
      setLoading(false);
    }
  }, [geocodeLocation]);

  // Initialize map
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [74.35, 31.58], // Lahore coordinates
      zoom: 12,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Display route on map when points are ready
  useEffect(() => {
    if (!map.current || !mapLoaded || routePoints.length < 2) return;

    // Clear previous markers and layers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    if (routeLayer.current) {
      if (map.current.getLayer(routeLayer.current)) map.current.removeLayer(routeLayer.current);
      if (map.current.getLayer(`${routeLayer.current}-arrows`)) map.current.removeLayer(`${routeLayer.current}-arrows`);
      if (map.current.getSource(routeLayer.current)) map.current.removeSource(routeLayer.current);
    }

    try {
      // Create route ID
      routeLayer.current = `route-${selectedRoute._id}-${Date.now()}`;

      // Get coordinates for the line
      const lineCoordinates = routePoints.map(point => point.coordinates);
      const bounds = new mapboxgl.LngLatBounds();

      // Add markers
      routePoints.forEach((point) => {
        const [lng, lat] = point.coordinates;
        bounds.extend([lng, lat]);

        const marker = new mapboxgl.Marker({
          element: createMarkerElement(point.type)
        })
          .setLngLat([lng, lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(`
              <div class="popup-content">
                <h3>${point.type === 'start' ? 'Start' : point.type === 'end' ? 'End' : 'Stop'}</h3>
                <p><strong>${point.originalName}</strong></p>
                ${point.placeName ? `<p>${point.placeName}</p>` : ''}
                <small>${lat.toFixed(6)}, ${lng.toFixed(6)}</small>
              </div>
            `)
          )
          .addTo(map.current);

        markers.current.push(marker);
      });

      // Add route line
      map.current.addSource(routeLayer.current, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: lineCoordinates
          }
        }
      });

      map.current.addLayer({
        id: routeLayer.current,
        type: 'line',
        source: routeLayer.current,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#3B82F6',
          'line-width': 4,
          'line-opacity': 0.9
        }
      });

      // Add direction arrows
      map.current.addLayer({
        id: `${routeLayer.current}-arrows`,
        type: 'symbol',
        source: routeLayer.current,
        layout: {
          'symbol-placement': 'line',
          'text-field': '▶',
          'text-size': 12,
          'symbol-spacing': 30,
          'text-keep-upright': false
        },
        paint: {
          'text-color': '#FFFFFF',
          'text-halo-color': '#3B82F6',
          'text-halo-width': 2
        }
      });

      // Fit bounds with padding
      map.current.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 100 },
        duration: 1000
      });

    } catch (error) {
      console.error('Error displaying route:', error);
      setError('Error displaying route on map');
    }
  }, [routePoints, mapLoaded, selectedRoute, createMarkerElement]);

  // When selected route changes
  useEffect(() => {
    if (selectedRoute) {
      processRoute(selectedRoute);
    }
  }, [selectedRoute, processRoute]);

  // Fetch routes on mount
  useEffect(() => {
    fetchRoutes();
  }, []);

  // Filter routes based on search
  const filteredRoutes = routes.filter(route =>
    route.routeNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.startPoint.toLowerCase().includes(searchTerm.toLowerCase()) ||
    route.endPoint.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="route-display-page">
      <div className="sidebar">
        <div className="sidebar-header">
          <h2><FaRoute /> Bus Routes</h2>
          <Link to="/admin-dashboard" className="back-button">
            Back to Dashboard
          </Link>
        </div>

        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search routes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {loading && (
          <div className="loading-overlay">
            <FaSpinner className="spinner" />
            <p>Loading route data...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <FaExclamationTriangle /> {error}
          </div>
        )}

        {geocodingErrors.length > 0 && (
          <div className="warning-message">
            <FaExclamationTriangle /> Could not locate: {geocodingErrors.map(e => e.location).join(', ')}
          </div>
        )}

        <div className="route-list">
          {filteredRoutes.map(route => (
            <div 
              key={route._id}
              className={`route-card ${selectedRoute?._id === route._id ? 'active' : ''}`}
              onClick={() => setSelectedRoute(route)}
            >
              <div className="route-icon">
                <FaBus />
              </div>
              <div className="route-info">
                <h3>Route {route.routeNo}</h3>
                <p><FaMapMarkerAlt /> {route.startPoint} to {route.endPoint}</p>
                <p>{route.stops.length} stops</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="map-container" ref={mapContainer}>
        {loading && (
          <div className="map-loading-overlay">
            <FaSpinner className="spinner" />
            <p>Loading map data...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteDisplayPage;